import { useCallback, useEffect, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { LeadProposalFullType, LeadProposalGenerationType } from '@econtractor/types';
import * as apiProposal from 'api/apiProposal';
import ProposalEditorOffcanvas from './ProposalEditorOffcanvas';
import TemplateDirSelectModal, { type TemplateDirOption } from '../share/TemplateDirSelectModal';
import { useToast } from 'context/ToastContext';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import { LeadApprovedProposal } from 'modules/lead';

interface LeadProposalPanelProps {
    leadId: number | string;
    leadEmail?: string;
    leadName?: string;
    leadStatus?: number;
    /** Lead record (client fields) for template key substitution. */
    objectData?: unknown;
    acceptedGeneration?: LeadProposalGenerationType | null;
    acceptedLoading?: boolean;
    onLeadUpdated?: () => Promise<void> | void;
    onProposalPresenceChange?: (hasProposal: boolean) => void;
    onRegisterOpen?: (open: (() => void) | null) => void;
}

const CREATE_NEW_DIR_ID = 0;

const LeadProposalPanel = ({
    leadId,
    leadEmail = '',
    leadName = '',
    leadStatus,
    objectData,
    acceptedGeneration = null,
    acceptedLoading = false,
    onLeadUpdated,
    onProposalPresenceChange,
    onRegisterOpen,
}: LeadProposalPanelProps) => {
    const { t } = useTranslation(['Proposal', 'common']);
    const { showToast } = useToast();
    const { auth } = useAuth();
    const canAccess = hasUserAccess(auth?.user, 'proposalCreate');
    const [leadProposal, setLeadProposal] = useState<LeadProposalFullType | null>(null);
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [dirOptions, setDirOptions] = useState<TemplateDirOption[]>([]);
    const [selectedDirId, setSelectedDirId] = useState<number>(0);
    const [editId, setEditId] = useState<number | null>(null);

    const fetchLeadProposal = async () => {
        const proposal = await apiProposal.getLeadProposal(leadId);
        setLeadProposal(proposal);
    };

    const fetchDirOptions = async () => {
        const result = await apiProposal.getDirList({ order: 'ORDER BY proposal_name', page: 1, onPage: 999999 });
        const options = (result?.proposals || []).map((row) => ({
            value: Number(row.ID),
            label: row.proposalName || `#${row.ID}`,
        }));
        setDirOptions(options);
        setSelectedDirId(CREATE_NEW_DIR_ID);
    };

    const handleOpen = useCallback(async () => {
        if (leadProposal?.ID) {
            setEditId(leadProposal.ID);
            return;
        }
        await fetchDirOptions();
        setShowSelectModal(true);
    }, [leadProposal?.ID, leadId]);

    const handleCreate = async () => {
        const result =
            selectedDirId === CREATE_NEW_DIR_ID
                ? await apiProposal.createEmptyLeadProposal(leadId)
                : await apiProposal.createLeadProposalFromDir(leadId, selectedDirId);
        if (!result || !result.leadProposalId) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        setShowSelectModal(false);
        await fetchLeadProposal();
        setEditId(result.leadProposalId);
    };

    const handleDelete = async () => {
        if (!leadProposal?.ID) return;
        const stat = await apiProposal.deleteLeadProposal(leadId, leadProposal.ID);
        if (!stat) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        showToast(t('lead.messDeleted'), 'success');
        setLeadProposal(null);
        setEditId(null);
        if (onLeadUpdated) await onLeadUpdated();
    };

    useEffect(() => {
        void fetchLeadProposal();
    }, [leadId]);

    useEffect(() => {
        onProposalPresenceChange?.(canAccess ? Boolean(leadProposal?.ID) : false);
    }, [canAccess, leadProposal?.ID, onProposalPresenceChange]);

    useEffect(() => {
        if (!canAccess) {
            onRegisterOpen?.(null);
            return;
        }
        onRegisterOpen?.(() => {
            void handleOpen();
        });
        return () => onRegisterOpen?.(null);
    }, [canAccess, handleOpen, onRegisterOpen]);

    if (!canAccess) return null;

    return (
        <>
            <ProposalEditorOffcanvas
                mode="lead"
                leadId={leadId}
                leadEmail={leadEmail}
                leadName={leadName}
                leadStatus={leadStatus}
                objectData={objectData}
                proposalId={editId}
                onClose={() => setEditId(null)}
                onSaved={fetchLeadProposal}
                onGenerated={onLeadUpdated}
            />

            <TemplateDirSelectModal
                show={showSelectModal}
                title={t('lead.selectTitle')}
                label={t('lead.selectLabel')}
                createNewLabel={t('lead.selectCreateNew')}
                options={dirOptions}
                selectedId={selectedDirId}
                onSelectedIdChange={setSelectedDirId}
                onHide={() => setShowSelectModal(false)}
                onConfirm={() => void handleCreate()}
            />

            <Card className="mt-3 lead-proposal-panel-card">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                        <div className="min-w-0">
                            <h6 className="mb-1">{t('lead.blockTitle')}</h6>
                            <div className="text-muted fs-12">
                                {leadProposal?.proposalName || t('lead.notAttached')}
                            </div>
                            <LeadApprovedProposal
                                compact
                                generation={acceptedGeneration}
                                loading={acceptedLoading}
                            />
                        </div>
                        <div className="d-flex gap-2">
                            <Button size="sm" variant="success" onClick={() => void handleOpen()}>
                                {t('lead.openButton')}
                            </Button>
                            <Button
                                size="sm"
                                variant="light-danger"
                                disabled={!leadProposal?.ID}
                                onClick={() => void handleDelete()}
                            >
                                {t('lead.deleteButton')}
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </>
    );
};

export default LeadProposalPanel;
